'use client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call } from '../client';
import { isApiError } from '../errors';
import type { Me } from '../schema';
import { useApi } from './context';
import { qk } from './keys';

/** The signed-in staff member, or null without a session (401). */
export function useMe() {
  const api = useApi();
  return useQuery<Me | null>({
    queryKey: qk.me(),
    queryFn: async ({ signal }) => {
      try {
        return await call(api.GET('/v1/auth/me', { signal }));
      } catch (e) {
        if (isApiError(e) && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** Where the browser goes to sign in (Keycloak via the API), landing back on returnTo. */
export function loginUrl(returnTo?: string, base = '/api'): string {
  const q = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : '';
  return `${base}/v1/auth/login${q}`;
}

/** Ends the session; returns Keycloak's end-session URL to open next (or null). */
export function useLogout() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => call(api.POST('/v1/auth/logout')),
    onSuccess: () => {
      qc.clear();
    },
  });
}

export function useInviteLookup(token: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['auth', 'invite', token],
    queryFn: () => call(api.POST('/v1/auth/invites/lookup', { body: { token: token ?? '' } })),
    enabled: Boolean(token),
    retry: false,
    staleTime: Infinity,
  });
}

export function useAcceptInvite() {
  const api = useApi();
  return useMutation({
    mutationFn: (body: { token: string; password: string; password_confirm: string }) =>
      call(api.POST('/v1/auth/invites/accept', { body })),
  });
}
